import { useEffect, useState } from "react";
// import AppLogo from './../assets/One-App.png';
import DcclLogo from "./../assets/DCCL-Logo.png";
import { useTheme } from "next-themes";
import toast from "react-hot-toast";
import { customToast } from "./../components/toasts";
import { useAuth } from "../store/AuthContext";
import { customLoginButton } from "../components/customStyles";
import { loginUserWithEmailAndPassword, loginUserWithOtp, verifyUserWithOtp } from "../services/auth.service";
import { Link, useNavigate } from "react-router-dom";
import { localStorageUtil, sessionStorageUtil } from "../utils/storage";
import { pick } from "../utils/object";
import { useTranslation } from "react-i18next";
import { useI18n } from "../hooks/useI18n";

import LogoDark from "../assets/InvestorApp.png";
import useWindowSize from "../hooks/useWindowSize";

const Login = () => {
  const [activeLoginMethod, setActiveLoginMethod] = useState("email");
  const [formProcessing, setFormProcessing] = useState(false);
  const [viewPassword, setViewPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [formStep, setFormStep] = useState(0);

  const { theme } = useTheme();

  const { isRTL } = useI18n();

  const { login } = useAuth();

  const { t } = useTranslation();
  const screenSize = useWindowSize();

  const navigate = useNavigate();

  const formatCNIC = (value) => {
    if (value.length === 16) return formData.cnic;

    let digits = "";

    for (const x of value) {
      if (x >= "0" && x <= "9") {
        digits += x;
      }
    }

    let formatted = "";

    [...digits].forEach((x, i) => {
      if (i === 5 || i === 12) {
        formatted += "-";
      }

      formatted += x;
    });

    return formatted;
  };

  const renderStep = () => {
    if (formStep == 0) {
      return (
        <input
          className='form-control text-center'
          placeholder={t("app.login.form.email")}
          name='email'
          value={formData.email}
          onChange={handleChange}
          type='text'
          style={{
            letterSpacing: "0.075em",
            backgroundColor: "rgb(230, 230, 230)",
            border: "1px solid rgb(250, 229, 255)",
            fontWeight: "600",
            fontSize: "1.1rem",
          }}
        />
      );
    } else if (formStep == 1) {
      return (
        <input
          className='form-control text-center'
          placeholder='OTP'
          name='otp'
          value={formData.otp}
          onChange={handleChange}
          type='text'
          style={{
            letterSpacing: "0.1em",
            backgroundColor: "rgb(230, 230, 230)",
            border: "1px solid rgb(250, 229, 255)",
            fontWeight: "600",
            fontSize: "1.1rem",
          }}
        />
      );
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!(formStep > 2)) setFormProcessing(true);

    let validationStatus = false;

    if (activeLoginMethod === "email") {
      if (!formData.email || !formData.password) {
        toast((ts) => customToast(t("auth.messages.error.missing"), ts), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            minWidth: "200px",
            backgroundColor: "rgba(255,255,255,0.85)",
          },
        });

        setFormProcessing(false);

        return;
      }

      try {
        const localFormattedCopy = {
          ...formData,
          email: formData.email.trim(),
        };
        const data = await loginUserWithEmailAndPassword(localFormattedCopy);

        if (data.type && data.type === "bypass") {
          toast((t) => customToast(data.message, t), {
            duration: 3000,
            removeDelay: 1000,
            style: {
              padding: "0.75rem",
              margin: "0",
              minWidth: "200px",
              backgroundColor: "rgba(255,255,255,0.85)",
            },
          });

          navigate("/auth/verify", { state: { email: formData.email, mobile_number: formData.mobile || "" } });

          return;
        }

        if (data.error || !data.data) {
          throw new Error(data.message || "Failed to Login");
        }

        toast((ts) => customToast(t("app.messages.login.success"), "success", ts), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            minWidth: "200px",
            backgroundColor: "rgb(255,255,255)",
          },
        });

        localStorageUtil.setRefreshToken(data.data?.refreshToken);
        sessionStorageUtil.setAccessToken(data.data?.token);

        const userData = pick(data.data, ["data", "documentsVerified", "requiresDocumentVerification"]);

        sessionStorageUtil.setUserData(userData);
        await new Promise((res) => setTimeout(() => res(login(userData)), 1000));

        setFormProcessing(false);

        navigate("/", { replace: true });
      } catch (error) {
        toast((t) => customToast(error.message, t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            minWidth: "200px",
            backgroundColor: "rgba(255,255,255,0.85)",
          },
        });
      } finally {
        setFormProcessing(false);
      }
    } else {
      switch (formStep) {
        case 0:
          if (!formData.email) {
            await new Promise((res) => setTimeout(() => res(), 1000));
            toast((ts) => customToast(t("auth.messages.error.email"), ts), {
              duration: 3000,
              removeDelay: 1000,
              style: {
                padding: "0.75rem",
                margin: "0",
                minWidth: "200px",
                backgroundColor: "rgba(255,255,255,0.85)",
              },
            });

            break;
          }

          await new Promise((res) => setTimeout(() => res(), 2000));

          try {
            const response = await loginUserWithOtp(formData.email.trim());

            if (response.error) {
              throw new Error(response.message);
            }

            toast((ts) => customToast(t("auth.messages.success.otp"), "success", ts), {
              duration: 3000,
              removeDelay: 1000,
              style: {
                padding: "0.75rem",
                margin: "0",
                minWidth: "200px",
                backgroundColor: "rgba(255,255,255,0.85)",
              },
            });
          } catch (error) {
            toast((t) => customToast(error.message, t), {
              duration: 3000,
              removeDelay: 1000,
              style: {
                padding: "0.75rem",
                margin: "0",
                minWidth: "200px",
                backgroundColor: "rgba(255,255,255,0.85)",
              },
            });
            break;
          }

          validationStatus = true;
          break;

        case 1:
          // if (!formData.password) break;

          await new Promise((res) => setTimeout(() => res(), 2000));

          try {
            const data = await verifyUserWithOtp(formData.email.trim(), formData.otp.trim());

            if (data.error || !data.data) {
              throw new Error(data.message || "Invalid Otp");
            }

            toast((t) => customToast(data.message, "success", t), {
              duration: 3000,
              removeDelay: 1000,
              style: {
                padding: "0.75rem",
                margin: "0",
                minWidth: "200px",
                backgroundColor: "rgb(255,255,255)",
              },
            });

            localStorageUtil.setRefreshToken(data.data?.refreshToken);
            sessionStorageUtil.setAccessToken(data.data?.token);

            const userData = pick(data.data, ["data", "documentsVerified", "requiresDocumentVerification"]);

            setFormProcessing(false);

            sessionStorageUtil.setUserData(userData);
            await new Promise((res) => setTimeout(() => res(login(userData)), 1000));

            navigate("/", { replace: true });
          } catch (error) {
            toast((t) => customToast(error.message, t), {
              duration: 3000,
              removeDelay: 1000,
              style: {
                padding: "0.75rem",
                margin: "0",
                minWidth: "200px",
                backgroundColor: "rgba(255,255,255,0.85)",
              },
            });
            break;
          }

          validationStatus = true;
          break;
      }
    }

    setFormProcessing(false);

    if (validationStatus) {
      setFormStep((prev) => prev + 1);
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "email") {
      // value = formatCNIC(value);
    } else if (name === "otp") {
      let formatted = "";

      for (const x of value) {
        if (x >= "0" && x <= "9") {
          formatted += x;
        }
      }

      if (formatted.length >= 8) {
        const newValue = formatted.slice(0, 8);

        formatted = newValue;
      }

      value = formatted;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFaceScan = async () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ audio: false, video: true })
        .then((stream) => {
          document.querySelector("#face-scan").srcObject = stream;
        })
        .catch((error) => {
          toast((ts) => customToast(t("auth.messages.error.camera"), ts), {
            duration: 3000,
            removeDelay: 1000,
            style: {
              padding: "0.75rem",
              margin: "0",
              minWidth: "200px",
              backgroundColor: "rgba(255,255,255,0.85)",
            },
          });
        });
    } else {
    }
  };

  const isMobileDevice = () => {
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile !== "undefined") {
      return navigator.userAgentData.mobile;
    }
    return (navigator.maxTouchPoints > 0 || "ontouchstart" in window) && window.matchMedia("(max-width: 768px)").matches;
  };

  useEffect(() => {
    setFormStep(0);
    if (activeLoginMethod === "email") {
      setFormData(() => ({
        email: "",
        password: "",
      }));
    } else {
      setFormData(() => ({
        email: "",
        otp: "",
      }));
    }
  }, [activeLoginMethod]);

  return (
    <div
      className={`add-custom-scroll${theme === "dark" ? "-dark" : ""} container-fluid w-100 d-flex mx-auto flex-column justify-content-start flex-nowrap`}
      style={{
        fontFamily: isRTL ? "var(--font-urdu)" : '"Barlow Condensed", "Kanit"',
        backgroundColor: "var(--theme-bg)",
        padding: screenSize <= 575 ? "8px 8px 8px 8px" : "4px 10px 10px 12px",
        borderRadius: "20px",
        rowGap: "8px",
        height: screenSize <= 575 ? "calc(100dvh - 148px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 202px)" : "calc(100dvh - 128px)",
        overflowY: "auto",
      }}
    >
      <div className='modal fade' id='exampleModal' tabIndex='-1' aria-labelledby='exampleModalLabel' aria-hidden='true'>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <video
              className='w-100 h-100'
              autoPlay
              id='face-scan'
              style={{
                backgroundColor: "black",
              }}
            />
          </div>
        </div>
      </div>

      <div className='row w-100 flex-column align-items-center justify-content-between flex-nowrap mx-auto py-3' style={{
        height: '100%',
        minHeight: '700px'
      }}>
        <div
          className='col-auto p-0 text-center d-flex flex-column justify-content-end pt-3'
          style={{
            flex: "2",
          }}
        >
          <img
            className=''
            src={LogoDark}
            style={{
              width: "300px",
              objectFit: "contain",
              maxWidth: "100%",
            }}
          />
          {/* <svg
            xmlns='http://www.w3.org/2000/svg'
            width={140}
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth={1.25}
            strokeLinecap='round'
            strokeLinejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-stack-2 mx-auto'
            style={{
              color: "rgba(255,255,255,0.85)",
              overflow: "visible",
            }}
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M12 4l-8 4l8 4l8 -4l-8 -4' />
            <path d='M4 12l8 4l8 -4' />
            <path d='M4 16l8 4l8 -4' />
          </svg>
          <div
            className=''
            style={{
              fontSize: "3rem",
              fontWeight: "700",
              letterSpacing: "0.02em",
              lineHeight: "1",
              fontFamily: "Barlow Condensed",
              color: "rgba(255, 255, 255, 0.9)",
            }}
          >
            One-App
          </div> */}
        </div>
        <div
          className='col-auto p-0 d-flex flex-column justify-content-center'
          style={{
            flex: "5 0 420px",
          }}
        >
          <div>
            <div
              className='text-center'
              style={{
                lineHeight: "1",
                fontSize: "1.2rem",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {t("app.login.message")}
            </div>

            <div
              className='d-flex w-100 align-items-center justify-content-center mt-3'
              style={{
                color: "rgb(255,255,255)",
                // // lineHeight: '1.1',
                letterSpacing: "0.02em",
                gap: "1.2rem",
              }}
            >

              <button
                className='scan-btn text-center p-0 m-0 border-0'
                type='button'
                onClick={() => setActiveLoginMethod("email")}
                style={{
                  background: "none",
                  color: "rgba(255,255,255, .75)",
                }}
              >
                <div
                  className='rounded-3'
                  style={
                    activeLoginMethod === "email"
                      ? {
                          backgroundColor: "rgba(0, 83, 186, .8)",
                          boxShadow: "rgba(126, 184, 255, .25) 0px -2px 6px 3px",
                        }
                      : {
                          background: "none",
                          boxShadow: "none",
                        }
                  }
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={60}
                    height={60}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={1.25}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-text-scan-2'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M4 8v-2a2 2 0 0 1 2 -2h2' />
                    <path d='M4 16v2a2 2 0 0 0 2 2h2' />
                    <path d='M16 4h2a2 2 0 0 1 2 2v2' />
                    <path d='M16 20h2a2 2 0 0 0 2 -2v-2' />
                    <path d='M8 12h8' />
                    <path d='M8 9h6' />
                    <path d='M8 15h4' />
                  </svg>
                </div>
                <div
                  className='mt-1'
                  style={{
                    color: "rgb(255,255,255)",
                    fontSize: "0.9rem",
                    lineHeight: "1.1",
                    fontWeight: "400",
                    letterSpacing: ".03em",
                  }}
                >
                  {t("app.login.optionA.one")}
                  <br />
                  {t("app.login.optionA.two")}
                </div>
              </button>

              <button
                className='scan-btn text-center p-0 m-0 border-0'
                type='button'
                onClick={() => setActiveLoginMethod("otp")}
                style={{
                  background: "none",
                  color: "rgba(255,255,255, .75)",
                }}
              >
                <div
                  className='rounded-3'
                  style={
                    activeLoginMethod === "otp"
                      ? {
                          backgroundColor: "rgba(0, 83, 186, .8)",
                          boxShadow: "rgba(126, 184, 255, .25) 0px -2px 6px 3px",
                        }
                      : {
                          background: "none",
                          boxShadow: "none",
                        }
                  }
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={60}
                    height={60}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={1.25}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-object-scan'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M4 8v-2a2 2 0 0 1 2 -2h2' />
                    <path d='M4 16v2a2 2 0 0 0 2 2h2' />
                    <path d='M16 4h2a2 2 0 0 1 2 2v2' />
                    <path d='M16 20h2a2 2 0 0 0 2 -2v-2' />
                    <path d='M8 8m0 2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-4a2 2 0 0 1 -2 -2z' />
                  </svg>
                </div>
                <div
                  className='mt-1'
                  style={{
                    color: "rgb(255,255,255)",
                    fontSize: "0.9rem",
                    lineHeight: "1.1",
                    fontWeight: "400",
                    letterSpacing: ".03em",
                  }}
                >
                  {t("app.login.optionB.one")}
                  <br />
                  {t("app.login.optionB.two")}
                </div>
              </button>
            </div>

            <form
              className=''
              onSubmit={handleFormSubmit}
              style={{
                marginTop: "2.5rem",
                minWidth: "280px",
              }}
            >
              {activeLoginMethod === "email" ? (
                <>
                  <input
                    className='form-control text-center'
                    placeholder={t("app.login.form.email")}
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    type='text'
                    style={{
                      letterSpacing: "0.075em",
                      backgroundColor: "rgb(230, 230, 230)",
                      border: "1px solid rgb(250, 229, 255)",
                      fontWeight: "600",
                      fontSize: "1.1rem",
                    }}
                  />
                  <div
                    className='position-relative'
                    style={{
                      marginTop: ".6rem",
                    }}
                  >
                    <input
                      className='form-control text-center'
                      placeholder={t("app.login.form.password")}
                      name='password'
                      value={formData.password}
                      onChange={handleChange}
                      type={viewPassword ? "text" : "password"}
                      style={{
                        letterSpacing: "0.075em",
                        backgroundColor: "rgb(230, 230, 230)",
                        border: "1px solid rgb(250, 229, 255)",
                        fontWeight: "600",
                        fontSize: "1.1rem",
                      }}
                    />
                    {formData.password?.length ? (
                      viewPassword ? (
                        <button
                          className='btn position-absolute top-0 end-0 h-100 d-flex align-items-center justify-content-center px-2'
                          type='button'
                          onClick={() => setViewPassword(false)}
                          style={{
                            color: "var(--theme-light-secondary) !important",
                            border: "none",
                            boxShadow: "none",
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={24}
                            height={24}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={1.5}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-eye'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0' />
                            <path d='M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6' />
                          </svg>
                        </button>
                      ) : (
                        <button
                          className='btn position-absolute top-0 end-0 h-100 d-flex align-items-center justify-content-center px-2'
                          type='button'
                          onClick={() => setViewPassword(true)}
                          style={{
                            color: "var(--theme-light-secondary) !important",
                            border: "none",
                            boxShadow: "none",
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={24}
                            height={24}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={1.5}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-eye-off'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M10.585 10.587a2 2 0 0 0 2.829 2.828' />
                            <path d='M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87' />
                            <path d='M3 3l18 18' />
                          </svg>
                        </button>
                      )
                    ) : (
                      ""
                    )}
                  </div>
                </>
              ) : (
                renderStep()
              )}
              <button
                className='btn mt-4 w-100 d-flex align-items-center justify-content-center gap-2'
                type='button'
                onClick={handleFormSubmit}
                disabled={formProcessing}
                style={{
                  ...customLoginButton,
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  letterSpacing: "0.02em",
                }}
              >
                {formProcessing ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      aria-hidden='true'
                      style={{
                        fontSize: "0.85rem",
                        height: "15px",
                        width: "15px",
                      }}
                    ></span>
                    <span role='status'>{t("app.login.form.loading")}</span>
                  </>
                ) : activeLoginMethod === "email" ? (
                  t("app.login.form.button")
                ) : formStep === 1 ? (
                  t("app.login.form.button")
                ) : (
                  t("modals.common.next")
                )}
              </button>
            </form>
            <div className='d-flex align-items-center justify-content-between mt-2'>
              <Link
                className=''
                to='/auth/signup'
                style={{
                  color: "rgb(190, 190, 190)",
                  letterSpacing: ".025em",
                  fontWeight: "500",
                }}
              >
                {t("app.login.form.register")}
              </Link>
              <Link
                className=''
                to='/auth/recover'
                style={{
                  color: "rgb(190, 190, 190)",
                  letterSpacing: ".025em",
                  fontWeight: "500",
                }}
              >
                {t("app.login.form.forgot")}
              </Link>
            </div>
          </div>
        </div>
        <div className='col-auto p-0'>
          <div
            className='text-center'
            style={{
              lineHeight: "1",
              fontSize: "1.2rem",
              color: "rgba(255,255,255,0.7)",
            }}
          >
            {t("app.login.form.power")}
          </div>
          <img
            className=''
            src={DcclLogo}
            alt='Logo'
            style={{
              width: "160px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
